import {
  SET_CART_ITEM_QUANTITY,
  UPDATE_CART_ITEM_QUANTITY,
  CLEAR_CART,
  CLEAR_PRODUCT_CART,
  ADD_CUSTOM_DESIGN,
  REMOVE_CUSTOM_DESIGN,
  UPDATE_CUSTOM_DESIGN_IMAGE,
  UPDATE_CUSTOM_DESIGN_QUANTITY,
  SET_CART_OPEN,
} from "./types";

/**
 * Set the quantity for a specific color in the cart
 * @param {string} productType - "qr-collar" or "gps-tracker"
 * @param {string} color - "orange", "cream", "black", or "pink"
 * @param {number} quantity - The quantity to set (0-15)
 */
export const setCartItemQuantity = (productType, color, quantity) => ({
  type: SET_CART_ITEM_QUANTITY,
  payload: { productType, color, quantity },
});

/**
 * Increment or decrement the quantity for a specific color
 * @param {string} productType - "qr-collar" or "gps-tracker"
 * @param {string} color - "orange", "cream", "black", or "pink"
 * @param {number} increment - The amount to add (can be negative)
 */
export const updateCartItemQuantity = (productType, color, increment) => ({
  type: UPDATE_CART_ITEM_QUANTITY,
  payload: { productType, color, increment },
});

/**
 * Clear the entire cart (both QR collar and GPS tracker)
 */
export const clearCart = () => ({
  type: CLEAR_CART,
});

/**
 * Clear the cart for a specific product type
 * @param {string} productType - "qr-collar" or "gps-tracker"
 */
export const clearProductCart = (productType) => ({
  type: CLEAR_PRODUCT_CART,
  payload: { productType },
});

/**
 * Add a new custom design slot
 * @param {string} productType - "qr-collar" or "gps-tracker"
 */
export const addCustomDesign = (productType) => ({
  type: ADD_CUSTOM_DESIGN,
  payload: { productType },
});

/**
 * Remove a custom design
 * @param {string} productType - "qr-collar" or "gps-tracker"
 * @param {number} designId - The ID of the custom design to remove
 */
export const removeCustomDesign = (productType, designId) => ({
  type: REMOVE_CUSTOM_DESIGN,
  payload: { productType, designId },
});

/**
 * Update the image for a custom design
 * @param {string} productType - "qr-collar" or "gps-tracker"
 * @param {number} designId - The ID of the custom design
 * @param {File} image - The image file
 * @param {string} preview - The preview URL
 */
export const updateCustomDesignImage = (productType, designId, image, preview) => ({
  type: UPDATE_CUSTOM_DESIGN_IMAGE,
  payload: { productType, designId, image, preview },
});

/**
 * Update the quantity for a custom design
 * @param {string} productType - "qr-collar" or "gps-tracker"
 * @param {number} designId - The ID of the custom design
 * @param {number} quantity - The new quantity (0-15)
 */
export const updateCustomDesignQuantity = (productType, designId, quantity) => ({
  type: UPDATE_CUSTOM_DESIGN_QUANTITY,
  payload: { productType, designId, quantity },
});

/**
 * Get the total quantity for a product type
 * @param {object} cartState - The cart state from Redux
 * @param {string} productType - "qr-collar" or "gps-tracker"
 * @returns {number} The total quantity
 */
export const getTotalQuantity = (cartState, productType) => {
  const cartKey = productType === "gps-tracker" ? "gpsTracker" : "qrCollar";
  const cart = cartState[cartKey];

  const standardTotal = Object.values(cart.colorQuantities).reduce(
    (sum, qty) => sum + qty,
    0
  );

  const customTotal = cart.customDesigns
    .filter((d) => d.image)
    .reduce((sum, d) => sum + (d.quantity || 0), 0);

  return standardTotal + customTotal;
};

/**
 * Get the total price for a product type
 * @param {object} cartState - The cart state from Redux
 * @param {string} productType - "qr-collar" or "gps-tracker"
 * @returns {number} The total price including shipping
 */
export const getTotalPrice = (cartState, productType) => {
  const unitPrice = productType === "gps-tracker" ? 205 : 35;
  const shippingPrice = 10;
  const totalQuantity = getTotalQuantity(cartState, productType);

  if (totalQuantity === 0) return 0;
  return unitPrice * totalQuantity + shippingPrice;
};

/**
 * Set the cart open/closed state
 * @param {boolean} isOpen - Whether the cart should be open
 */
export const setCartOpen = (isOpen) => ({
  type: SET_CART_OPEN,
  payload: isOpen,
});
